import { z } from 'zod';

import dayjs from 'utils/extendedDayJS';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { convertSendAtToFormValue } from 'pages/dashboard/overview/events/schemas/commonUtils';

export const FEEDBACK_REQUEST_TYPE = {
  NOW: 'IMMEDIATE',
  SCHEDULE: 'SCHEDULED'
} as const;

/**
 * Form Data Structure:
 * {
 *   type: 'IMMEDIATE' | 'SCHEDULED';
 *   sendAt?: Date;  // Date in form
 *   notificationChannel: {
 *     email: boolean;
 *     slack: boolean;
 *   };
 * }
 *
 * Database Data Structure:
 * Same as above but:
 *  - sendAt is string in UTC format
 */

const NotificationChannelSchema = z
  .object({
    email: z.boolean(),
    slack: z.boolean()
  })
  .superRefine((data, ctx) => {
    if (!data.email && !data.slack) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must have one notification method selected',
        path: ['slack']
      });
    }
  });

export const resendFeedbackFormSchema = (eventTimezone?: string) =>
  z
    .object({
      type: z.enum([FEEDBACK_REQUEST_TYPE.NOW, FEEDBACK_REQUEST_TYPE.SCHEDULE]),
      sendAt: z.date().transform((date: Date): string => {
        if (!date) return dayjs.formatForDB(dayjs.utc());
        // For outgoing data:
        // 1. Date is in browser's timezone (from DateTimePickerOriginal)
        // 2. Replace timezone to event's timezone (keep wall-clock)
        // 3. Convert to UTC for DB
        const wallClockDate = dayjs(date).format(FULL_DATE_FORMAT);
        const inEventTz = dayjs.tz(wallClockDate, eventTimezone ?? 'UTC').toDate();
        return dayjs.formatForDB(inEventTz);
      }),
      notificationChannel: NotificationChannelSchema
    })
    .superRefine((data, ctx) => {
      if (!data.sendAt && data.type === FEEDBACK_REQUEST_TYPE.SCHEDULE) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled time is required',
          path: ['sendAt']
        });
      }

      // Must be in the future
      if (
        data.sendAt &&
        data.type === FEEDBACK_REQUEST_TYPE.SCHEDULE &&
        dayjs.utc(data.sendAt).isBefore(dayjs.utc())
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled time must be in the future',
          path: ['sendAt']
        });
      }
      return true;
    });

export type resendFeedbackFormInput = z.input<ReturnType<typeof resendFeedbackFormSchema>>; // Form values
export type resendFeedbackFormValues = z.infer<ReturnType<typeof resendFeedbackFormSchema>>; // DB values

export const getResendFeedbackDefaults = (eventTimezone?: string): resendFeedbackFormInput => {
  return {
    type: FEEDBACK_REQUEST_TYPE.NOW,
    sendAt: convertSendAtToFormValue(dayjs.utc().toISOString(), eventTimezone),
    notificationChannel: {
      email: true,
      slack: true
    }
  };
};

export const convertDBToFormData = (
  dbData: resendFeedbackFormValues,
  eventTimezone?: string
): resendFeedbackFormInput => {
  return {
    ...dbData,
    sendAt: convertSendAtToFormValue(dbData.sendAt, eventTimezone)
  };
};
